import PropTypes from "prop-types";
import React from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";

/**
 * Deletion confirmation that spells out the consequences.
 *
 * `DeleteModal` asks a generic "are you sure?" with no context and stays
 * clickable while the request runs, so a double click fires two deletes. This
 * one names the record, lists what the deletion implies (cascade, attached
 * records), and blocks the action outright when it is known to be refused.
 */
const ConfirmDeleteModal = ({
  show,
  onDeleteClick,
  onCloseClick,
  title = "Confirmer la suppression",
  itemLabel = null,
  implications = [],
  blockedReason = null,
  isPending = false,
  confirmLabel = "Je confirme",
}) => {
  const isBlocked = Boolean(blockedReason);

  return (
    <Modal isOpen={show} toggle={onCloseClick} centered={true}>
      <ModalBody className="py-3 px-5">
        <div className="mt-2 text-center">
          <lord-icon
            src="https://cdn.lordicon.com/gsqxdxog.json"
            trigger="loop"
            colors="primary:#f7b84b,secondary:#f06548"
            style={{ width: "90px", height: "90px" }}
          ></lord-icon>
          <div className="mt-4 pt-2 fs-15">
            <h4>{title}</h4>
            {itemLabel ? (
              <p className="text-muted mb-0">
                <strong>{itemLabel}</strong>
              </p>
            ) : null}
          </div>
        </div>

        {isBlocked ? (
          <div className="alert alert-danger mt-4 mb-0" role="alert">
            <i className="ri-error-warning-line align-bottom me-1"></i>
            {blockedReason}
          </div>
        ) : implications.length > 0 ? (
          <div className="alert alert-warning mt-4 mb-0" role="alert">
            <p className="mb-2 fw-semibold">
              <i className="ri-alert-line align-bottom me-1"></i>
              Cette action est irréversible et implique :
            </p>
            <ul className="mb-0 ps-3">
              {implications.map((implication) => (
                <li key={implication}>{implication}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-muted text-center mt-4 mb-0">
            Cette action est irréversible.
          </p>
        )}

        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
          <button
            type="button"
            className="btn w-sm btn-light"
            onClick={onCloseClick}
            disabled={isPending}
          >
            {isBlocked ? "Fermer" : "Annuler"}
          </button>
          {isBlocked ? null : (
            <button
              type="button"
              className="btn w-sm btn-danger"
              id="delete-record"
              onClick={onDeleteClick}
              disabled={isPending}
            >
              {isPending ? <Spinner size="sm" className="me-2" /> : null}
              {confirmLabel}
            </button>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

ConfirmDeleteModal.propTypes = {
  show: PropTypes.any,
  onDeleteClick: PropTypes.func,
  onCloseClick: PropTypes.func,
  title: PropTypes.string,
  itemLabel: PropTypes.string,
  implications: PropTypes.arrayOf(PropTypes.string),
  blockedReason: PropTypes.string,
  isPending: PropTypes.bool,
  confirmLabel: PropTypes.string,
};

export default ConfirmDeleteModal;
